import { type Dispatch, type SetStateAction, useCallback, useState } from 'react'

type UseSessionStorageOptions<T> = {
  key: string
  defaultValue?: T
}

type UseSessionStorageReturn<T> = readonly [T, Dispatch<SetStateAction<T>>, () => void]

function readSessionStorageValue<T>(key: string, defaultValue: T): T {
  try {
    const storedValue = sessionStorage.getItem(key)
    return storedValue === null ? defaultValue : (JSON.parse(storedValue) as T)
  } catch {
    return defaultValue
  }
}

export function useSessionStorage<T>({
  key,
  defaultValue,
}: UseSessionStorageOptions<T> & { defaultValue: T }): UseSessionStorageReturn<T>
export function useSessionStorage<T>({
  key,
  defaultValue,
}: UseSessionStorageOptions<T>): UseSessionStorageReturn<T | undefined>
export function useSessionStorage<T>({
  key,
  defaultValue,
}: UseSessionStorageOptions<T>): UseSessionStorageReturn<T | undefined> {
  const [state, setState] = useState(() => ({
    key,
    value: readSessionStorageValue(key, defaultValue),
  }))
  const value = state.key === key ? state.value : readSessionStorageValue(key, defaultValue)

  const setStoredValue: Dispatch<SetStateAction<T | undefined>> = useCallback(
    (nextValue) => {
      setState((currentState) => {
        const currentValue =
          currentState.key === key ? currentState.value : readSessionStorageValue(key, defaultValue)
        const resolvedValue =
          typeof nextValue === 'function'
            ? (nextValue as (current: T | undefined) => T | undefined)(currentValue)
            : nextValue

        try {
          if (resolvedValue === undefined) {
            sessionStorage.removeItem(key)
          } else {
            sessionStorage.setItem(key, JSON.stringify(resolvedValue))
          }
        } catch {
          return { key, value: resolvedValue }
        }

        return { key, value: resolvedValue }
      })
    },
    [defaultValue, key]
  )

  const removeValue = useCallback(() => {
    try {
      sessionStorage.removeItem(key)
    } catch {
      setState({ key, value: defaultValue })
      return
    }
    setState({ key, value: defaultValue })
  }, [defaultValue, key])

  return [value, setStoredValue, removeValue] as const
}
